import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import Footer from "../components/Footer";
import { ShieldCheck, BadgeCheck, ArrowRight, Building2, Target, FileCheck2 } from "lucide-react";

// Full public verification page that the embeddable badge links to.
export default function VerifiedPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    document.documentElement.classList.add("dark");
    api.get(`/onboarding/verified/${slug}`).then((r) => setData(r.data)).catch(() => setErr(true));
  }, [slug]);

  if (err) {
    return (
      <div className="min-h-screen bg-background text-foreground dark flex items-center justify-center p-6" data-testid="verified-not-found">
        <div className="text-center">
          <div className="font-display text-2xl font-bold">Organisation not found</div>
          <p className="text-muted-foreground text-sm mt-2">This verification link is invalid or the organisation is no longer listed.</p>
          <Link to="/welcome"><Button variant="outline" className="mt-5">Go to Lestari</Button></Link>
        </div>
      </div>
    );
  }
  if (!data) return <div className="min-h-screen bg-background dark" />;

  const statusLabel = data.subscription_status === "active" ? "Active subscriber"
    : data.subscription_status === "trialing" ? "On trial" : "ESG member";

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground dark grid-bg" data-testid="verified-page">
      <div className="max-w-2xl w-full mx-auto px-6 py-16 flex-1">
        <Link to="/welcome" className="flex items-center gap-2.5 mb-10" data-testid="verified-brand">
          <div className="h-8 w-8 bg-accent flex items-center justify-center rounded-sm">
            <ShieldCheck className="h-4 w-4 text-black" strokeWidth={2.5} />
          </div>
          <span className="font-display font-bold tracking-tight text-lg">LESTARI</span>
        </Link>

        <div className="card-flat bg-card p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-14 w-14 rounded-sm bg-accent flex items-center justify-center shrink-0">
              <BadgeCheck className="h-8 w-8 text-black" strokeWidth={2.2} />
            </div>
            <div>
              <div className="label-eyebrow text-accent">Verified by Lestari</div>
              <h1 className="font-display text-2xl font-bold tracking-tight leading-tight">{data.name}</h1>
            </div>
          </div>

          <Badge variant="success" className="mb-6">{statusLabel}{data.member_since ? ` · since ${data.member_since}` : ""}</Badge>

          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-4 text-sm">
            <Row icon={Building2} label="UEN" value={data.uen} />
            <Row icon={Building2} label="Entity category" value={data.entity_category} />
            <Row icon={Building2} label="Industry" value={data.industry_name || data.sector} />
            <Row icon={FileCheck2} label="Regulatory tier" value={data.regulatory_tier} />
            <Row icon={Target} label="Material topics tracked" value={data.materiality_topics} />
            <Row icon={FileCheck2} label="Frameworks" value={(data.frameworks || []).join(", ") || "—"} />
          </div>

          <div className="border-t border-border mt-6 pt-5 text-xs text-muted-foreground leading-relaxed">
            This organisation has completed identity and entity verification on Lestari and maintains an active ESG
            workspace. Verification reflects platform onboarding status, not an audit opinion.
          </div>
        </div>

        <div className="card-flat bg-surface p-6 mt-5 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div>
            <div className="font-semibold text-sm">Want a Verified by Lestari badge?</div>
            <div className="text-xs text-muted-foreground mt-0.5">Provision your organisation in minutes — free for 15 days.</div>
          </div>
          <Link to="/get-started"><Button variant="primary" className="gap-2" data-testid="verified-cta">Start free trial <ArrowRight className="h-4 w-4" /></Button></Link>
        </div>
      </div>
      <Footer />
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div>
      <div className="label-eyebrow flex items-center gap-1.5"><Icon className="h-3 w-3" /> {label}</div>
      <div className="font-mono text-sm mt-1">{value ?? "—"}</div>
    </div>
  );
}
